class Factory {
  fn: Function; // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  context: any;
  args: any[];
  newContext: boolean;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  constructor(fn: Function, {context = null, args = [] as unknown[], newContext = false}: {context?: unknown, args?: unknown[], newContext?: boolean}) {
    this.fn = fn;
    this.context = context;
    this.args = args;
    this.newContext = newContext;
  }

  create() {
    if (this.newContext) {
      // Like calling `new this.fn(...this.args)` but without the `new` keyword.
      const ctx = Object.create(this.fn.prototype);
      return this.fn.apply(ctx, this.args);
    }
    return this.fn.apply(this.context, this.args);
  }
}

/**
 * Dependency injection container.
 */
export class Container {
  factories: Map<string, Factory>;
  instances: Map<string, any>;
  lookupStack: string[];
  cacheMaps: Map<string, Map<any, any>>;

  constructor() {
    this.factories = new Map();
    this.instances = new Map();
    /**
     * Stack of names that are currently being looked up, used to detect lookup
     * cycles.
     */
    this.lookupStack = [];
    /**
     * cache_name -> Map
     *
     * This is a separate namespace from the factory registry.
     */
    this.cacheMaps = new Map();
  }

  /**
   * Register a factory function or class for the given name.
   * @param {string} name The name to register, which can be looked up with inject()
   * @param {Function} factoryFn Used to instantiate when something looks up the name with inject().  The return value will be cached.
   * @param {object} [options]
   * @param {any} [options.context] The context that factoryFn should be called with
   * @param {Array<any>} [options.args] Arguments for factoryFn
   * @param {boolean} [options.newContext] true to call factoryFn with the new keyword
   */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  register(name: string, factoryFn: Function, options: {context?: unknown, args?: unknown[], newContext?: boolean} = {}) {
    this.factories.set(name, new Factory(factoryFn, options));
  }

  /**
   * Look up an instance that was registered for the given name.
   * @param {string} name The name to look up
   * @return {any} The instance created, which is cached as a singleton
   */
  lookup(name: string) {
    const cached = this.instances.get(name);
    if (cached !== undefined) {
      return cached;
    }

    // Check for circular lookup.
    if (this.lookupStack.includes(name)) {
      throw new Error(`circular lookup: ${this.lookupStack.join(' => ')} => ${name}`);
    }

    // Record lookup.
    this.lookupStack.push(name);

    try {
      // Look up the factory.
      const factory = this.factoryFor(name);
      if (!factory) {
        throw new Error(`factory not registered for name: ${name}`);
      }

      // Instantiate.  This may recursively lookup.
      const instance = factory.create();

      // Cache the instance.
      this.instances.set(name, instance);

      return instance;
    } finally {
      this.lookupStack.pop();
    }
  }

  /**
   * Get the factory for a name.
   * @param {string} name The name to look up
   * @return {Factory | undefined} The factory for the name, or undefined if the
   * name wasn't registered
   */
  factoryFor(name: string) {
    return this.factories.get(name);
  }

  /**
    * Get or create a cache of the given name.  This could be done with
    * register() and lookup(), but it's a convenience to handle the common case
    * where you want a Map scoped to the container that acts as a cache.
    *
    * @param {string} cacheName Name of the cache, for example, the name of the
    * class that uses it.
    * @return {Map<any, any>} The cache
    */
  cacheFor(cacheName: string) {
    const cached = this.cacheMaps.get(cacheName);
    if (cached !== undefined) {
      return cached;
    }

    const cache = new Map();
    this.cacheMaps.set(cacheName, cache);

    return cache;
  }
}

/**
 * Singleton
 * @type {Container}
 */
export const globalContainer = new Container();
